import './Phi.scss';
import { utility } from '@phi/components';

import { defineComponent, PropType, watch, h } from 'vue';
import { debounce } from '@vaaas/fpts/timing';
import { into } from '@vaaas/fpts/object';

import { version } from '@phi/constants';

import Scene from './Scene';
import Logo from './Logo';
import TopBar from './TopBar';
import LeftBar from './LeftBar';
import BottomBar from './BottomBar';
import Aside from './Aside';
import { withSpinner } from '../state/spinner';
import { on_key_down, on_key_up } from '../keypress';
import CatastrophicError from './CatastrophicError';
import Modals from './Modals';

import { refresh_model_modal } from '../input_output';
import { set_button_states } from '../non_graphical_ui_actions';
import { merge_keymaps } from '../keyboard';
import * as keymaps from '../keymaps';
import { load_module } from '@phi/jcore/module';
import { init } from '../scene';
import { preparatory_work } from '../initialization_and_event_handling';
import {
    saveSettings,
    shareWithEdu,
    log,
} from '@phi/frontend/api/calls';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { nice_version_format, pretty_print_record } from '@phi/util';
import { activate_mode, change_major_mode, deactivate_mode } from '../state/mode';
import { pointer_up } from '../pointer';
import { ApplicationContainer } from '../application-container';
import { count_of_open_modals, in_tutorial } from '../state';
import { ENV } from '@phi/config/frontend';
import { AxesIndicators } from '@features/indicate-axes';
import {
    LongPressIndicator,
    TouchIndicator,
    should_indicate_touches,
} from '@phi/touch';
import { vif } from '@phi/util/vuetils';
import { VertexIndicator } from '@features/indicate-vertices';
import { open_in_new_tab } from '@phi/util/window';
import { IFrontendServiceContainer } from '@phi/frontend/service-container';
import { reference_transparent } from '@features/references';
import { from } from '@phi/newtype';

const current_year = new Date().getFullYear();

export default defineComponent({
    props: {
        app: {
            required: true,
            type: Object as PropType<ApplicationContainer>,
        },
        service_container: {
            required: true,
            type: Object as PropType<IFrontendServiceContainer>,
        },
    },

    async created() {
        await this.load_core();
        this.setup_jquery();
        if (this.service_container.authentication.session_type.value.has('onshape')) {
            await this.service_container.authentication.authenticate_through_onshape();
        }

        // setup scene
        init(true, 100);
        preparatory_work();
        this.app.state.in_debug_mode = true;

        this.setup_watchers();
        change_major_mode(keymaps.normal);
        this.initial_logging();

        this.app.state.loaded = true;

        if (this.service_container.state.user?.email)
            this.service_container.analytics.log_user_login(
                this.service_container.state.user.email,
                this.service_container.authentication.session_type.value.has('phi')
                    ? 'phi'
                    : 'onshape'
            );
    },

    mounted() {
        document.addEventListener('pointerup', pointer_up);
        document.addEventListener('focusout', this.on_focus_change);
    },

    unmounted() {
        document.removeEventListener('pointerup', pointer_up);
        document.removeEventListener('focusout', this.on_focus_change);
    },

    render() {
        if (this.app.state.catastrophic_error)
            return h(CatastrophicError, { message: this.app.state.catastrophic_error });

        const state = into(this.app.state);

        return [
            h('div', {
                id: 'phi',
                class: {
                    phi: true,
                    [utility.pointer_no]: true,
                    demo: this.app.state.is_demo,
                    immersive: this.app.state.immersive,
                    'no-labels': !this.app.settings.labels,
                    tutorial: in_tutorial(),
                    ['tutorial--step-' + this.app.state.tut_stage]: true,
                    wait: this.app.state.spinner !== undefined,
                    dragging:
                        this.app.state.dragging_type !== 'none'
                        || this.app.state.panning
                        || this.app.state.rotating
                        || this.app.state.carving
                        || this.app.state.drawing_edge,
                },
            }, [
                vif(should_indicate_touches())(h(TouchIndicator)),
                h(LongPressIndicator),
                h(Scene, {
                    background: this.app.settings.background,
                    triangles: this.app.state.triangles,
                    lights: this.app.settings.lights,
                    inside_red: this.app.state.inside_red,
                    environment: this.app.settings.environment,
                    display_units: this.app.state.display_units,
                    reference_transparent: reference_transparent.value,
                    hidden_edges_visible: this.app.state.hidden_edges_visible,
                    wireframe_visible: this.app.state.wireframe_visible,
                    inset: this.app.settings.inset && !this.app.state.immersive,
                    state: this.app.state,
                }),
                h(Logo, {
                    immersive: this.app.state.immersive,
                    'onUpdate:immersive': state('immersive'),
                    light: this.service_container.util.dark_mode_enabled(),
                }),
                h('div', { class: 'copyrights' }, `Phi v${nice_version_format(version)} - © ${current_year} Phenometry PC`),
                h(TopBar, { app: this.service_container }),
                h(LeftBar, {
                    settings: this.app.settings,
                    state: this.app.state,
                    app: this.service_container,
                }),
                h(BottomBar, {
                    settings: this.app.settings,
                    state: this.app.state,
                    app: this.service_container,
                }),
                h(AxesIndicators, {
                    visible: this.app.state.axes_visible,
                }),
                h(VertexIndicator),
            ]),
            h(Modals, {
                state: this.app.state,
                settings: this.app.settings,
                app: this.service_container,
            }),
            h(Aside, {
                aside: this.app.state.aside,
                is_demo: this.app.state.is_demo,
                tut_stage: this.app.state.tut_stage,
                'onUpdate:aside': state('aside'),
                'onUpdate:tut_stage': state('tut_stage'),
            }),
        ];
    },

    methods: {
        async saveSettings(): Promise<void> {
            if (this.service_container.authentication.is_authorised())
                await saveSettings(this.app.settings).catch(e => {
                    console.log(e);
                    display_message_to_user(LOG.WARN, 'Failed to save settings.');
                });
        },

        reset_scroll_position() {
            window.scrollTo(0, 0);
            document.body.scrollTop = 0;
        },

        /** attempt to fix an ios virtual keyboard related bug
         *
         * when the keyboard spawns, the viewport is scrolled up, but it's possible safari fails to scroll down again
         */
        on_focus_change(e: FocusEvent) {
            if (e.target instanceof HTMLInputElement)
                this.reset_scroll_position();
        },

        load_core(): Promise<void|Error> {
            return withSpinner('loading core...', () => load_module().then((Module) => {
                if (Module instanceof Error)
                    this.app.state.catastrophic_error  = 'Could not load Phi core';
                else
                    // TODO: inject this dependency so that it isn't a global variable!
                    //@ts-ignore
                    window.Module = Module;
            }))();
        },

        setup_jquery(): void {
            // @ts-ignore
            $('.draggable').draggable();
            // @ts-ignore
            $('.draggable').resizable();
            $('a', '.demo').click(function () { return false; });

            // Tutorials
            // @ts-ignore
            $('.modal-content').draggable({
                handle: '.modal-header',
                containment: 'document',
                cursor: 'move'
            });
        },

        setup_watchers(): void {
            watch(
                this.app.settings,
                debounce(
                    this.saveSettings.bind(this),
                    ENV === 'development' ? 1_000 : 60_000,
                ),
            );

            watch(
                () => this.app.settings.display_units,
                (value) => this.app.state.display_units = value,
                {
                    immediate: true,
                }
            );

            watch(
                () => [this.app.settings.inside_red, this.app.state.hidden_edges_visible] as [boolean, boolean],
                ([inside_red, hidden_edges]) => {
                    this.app.state.inside_red = inside_red && !hidden_edges;
                },
                {
                    immediate: true,
                },
            );

            watch(
                () => this.app.state.framework,
                (x) => {
                    this.app.state.is_demo = x === undefined;
                },
            );

            watch(
                () => this.app.state.shortcuts,
                (x) => {
                    if (x) {
                        document.addEventListener('keydown', on_key_down, false);
                        document.addEventListener('keyup', on_key_up, false);
                    } else {
                        document.removeEventListener('keydown', on_key_down);
                        document.removeEventListener('keyup', on_key_up);
                    }
                },
                {
                    immediate: true,
                }
            );

            watch(
                () => [
                    this.app.state.current_filename,
                    this.app.state.is_demo,
                    this.app.state.file_save_status
                ] as const,
                ([name, demo, status]) => {
                    let x: string;
                    if (demo)
                        x = 'Phi Demo';
                    else if (status === 'dirty')
                        x = `Phi - ${name}*`;
                    else
                        x = `Phi - ${name}`;
                    document.title = x;
                },
            );

            watch(
                () => this.app.state.onshape_default_length,
                (value) => {
                    if (value && value !== this.app.settings.display_units)
                        this.app.settings.display_units = value;
                }
            );

            watch(
                () => count_of_open_modals(),
                (value) => {
                    // block zoom when any modal is shown
                    this.app.state.shortcuts = value === 0;
                },
                {
                    immediate: true,
                }
            );

            watch(
                () => this.app.state.tut_stage,
                (value, oldValue) => {
                    if (value === oldValue) return;
                    if (value === 100) {
                        this.$emit('update:aside', undefined);
                    } else {
                        this.$emit('update:aside', 'tutorials');
                        if (uid_vertices.length === 0)
                            setTimeout(() => refresh_model_modal(), 1000);
                    }
                    set_button_states();
                },
            );

            watch(
                () => this.app.state.modes,
                (modes) => {
                    this.app.state.keymap = merge_keymaps(modes);
                },
                {
                    immediate: true,
                }
            );

            watch(
                () => this.app.state.editing_reference_partstudio,
                (partstudio) => {
                    if (partstudio)
                        activate_mode(keymaps.partstudio);
                    else
                        deactivate_mode(keymaps.partstudio);
                },
            );

            watch(
                () => this.app.state.editing_reference_stl,
                (stl) => {
                    if (stl)
                        activate_mode(keymaps.partstudio);
                    else
                        deactivate_mode(keymaps.partstudio);
                },
            );

            watch(
                () => this.app.state.rigid_move_manipulator,
                (rigid) => {
                    if (rigid)
                        activate_mode(keymaps.manipulator);
                    else
                        deactivate_mode(keymaps.manipulator);
                },
            );

            watch(
                () => this.app.state.attaching_to_stl,
                (attaching) => {
                    if (attaching)
                        activate_mode(keymaps.attaching_to_stl);
                    else
                        deactivate_mode(keymaps.attaching_to_stl);
                },
            );

            watch(
                () => this.app.deviceStorage.user_subscription_status,
                (value) => {
                    this.app.state.edu = value === 'educational';
                },
                {
                    immediate: true,
                }
            );

            watch(
                () => [this.app.state.edu, this.app.state.document] as const,
                ([educational, document]) => {
                    if (educational && document)
                        shareWithEdu(document).then(() => console.log('Successfully shared document'));
                },
                {
                    immediate: true,
                },
            );
        },

        initial_logging(): void {
            switch (this.app.deviceStorage.user_subscription_status) {
                case undefined:
                    console.log('Onshape/Phi subscription status: unknown');
                    break;

                default:
                    console.log('Onshape/Phi subscription status: Valid');
                    break;
            }

            switch (this.app.state.warning) {
                case 'must-be-public':
                    display_message_to_user(LOG.WARN, 'Private document editing is not allowed in Phi Free plan. Click here for more info: https://phi3d.com/pricing', true, () => open_in_new_tab('https://phi3d.com/pricing'));
                    break;
            }

            const user_info_for_logging = {
                Email: this.app.state.user?.email ? from(this.app.state.user.email) : '(empty field)',
                Name:
                    this.app.state.user?.first_name && this.app.state.user?.last_name
                        ? `${this.app.state.user.first_name} ${this.app.state.user.last_name}`
                        : '(empty field)',
                Company: this.app.state.user?.company ?? '(empty field)',
                Verified: this.app.state.user?.verified ? 'yes' : 'no',
            };

            switch (this.app.state.framework) {
                case 'onshape':
                    log('onshape iframe user:', pretty_print_record(user_info_for_logging));
                    break;

                case undefined:
                    log(`${Array.from(this.service_container.authentication.session_type.value).join(' & ')} standalone user:`, pretty_print_record(user_info_for_logging));
                    break;
            }
        }
    }
});
